const KEY = 'login_redirect'
const LOGIN_PATH = /^\/[^/]+\/login(\/|$)/

// 登录成功后 onLoginSuccess 与 LoginPopowerProvider 的兜底跳转可能先后触发，
// 缓存首次读取结果，保证多处调用拿到同一目标地址（跳转是整页加载，模块会随之重置）
let consumed = false
let cachedTarget: string | null = null
let redirectingToLogin = false

/** 只允许站内相对路径，并排除登录页，避免开放重定向和回跳循环 */
export function isSafeRedirect(target: string | null | undefined): target is string {
  if (!target || !target.startsWith('/') || target.startsWith('//') || target.startsWith('/\\')) return false
  const path = target.split(/[?#]/)[0]
  return !LOGIN_PATH.test(path)
}

export function safeRedirectParam(value: string | null | undefined): string | null {
  return isSafeRedirect(value) ? value : null
}

/** 把已校验的回跳地址挂到站内链接上，登录流程内跳转时不丢掉 redirect */
export function withRedirectQuery(href: string, redirect: string | null | undefined) {
  const target = safeRedirectParam(redirect)
  if (!target) return href
  const hashIndex = href.indexOf('#')
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : ''
  const base = hashIndex >= 0 ? href.slice(0, hashIndex) : href
  const join = base.includes('?') ? '&' : '?'
  return `${base}${join}redirect=${encodeURIComponent(target)}${hash}`
}

/**
 * 记录当前完整地址（路径 + 查询参数 + hash），供登录成功后回跳。
 */
export function saveLoginRedirect() {
  try {
    const { pathname, search, hash } = window.location
    // 登录相关页面本身不作为回跳目标
    if (LOGIN_PATH.test(pathname)) return
    sessionStorage.setItem(KEY, `${pathname}${search}${hash}`)
  } catch { /* sessionStorage 不可用时静默降级为跳首页 */ }
}

/**
 * 未登录访问受保护页面时整页去登录，地址形如 /en/login?redirect=/en/profile/edit/account。
 * 同时写入 sessionStorage，邮箱登录等子页面丢掉 query 时仍能回跳。
 */
export function redirectToLogin(locale: string) {
  if (typeof window === 'undefined' || redirectingToLogin) return
  const { pathname, search, hash } = window.location
  if (LOGIN_PATH.test(pathname)) return
  redirectingToLogin = true
  const target = `${pathname}${search}${hash}`
  saveLoginRedirect()
  const url = isSafeRedirect(target)
    ? `/${locale}/login?redirect=${encodeURIComponent(target)}`
    : `/${locale}/login`
  window.location.replace(url)
}

/**
 * 取出并清除记录的回跳地址。优先使用地址栏 redirect，其次 sessionStorage。
 */
export function consumeLoginRedirect(): string | null {
  if (consumed) return cachedTarget
  consumed = true
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('redirect')
    const stored = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
    const target = isSafeRedirect(fromQuery) ? fromQuery : stored
    if (isSafeRedirect(target)) cachedTarget = target
  } catch { /* ignore */ }
  return cachedTarget
}
